"use client";

import { useEffect } from "react";

import { Button } from "@heroui/button";
import { Card, CardBody, CardFooter } from "@heroui/card";
import { Divider } from "@heroui/divider";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { isChunkLoadError } from "@/lib/observability/expected-errors";
import { routes } from "@/lib/routes";

/**
 * Next.js route error boundary. Reports uncaught route errors to Rollbar and
 * PostHog via the stratum observability pipeline (no @rollbar/react needed).
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void EventPublisher.publishError(EventKeys.APP_ROUTE_ERROR, error, {
      digest: error.digest,
    });
  }, [error]);

  return (
    <div
      className="flex items-center justify-center min-h-[60vh] px-4"
      data-testid="route-error-boundary"
    >
      <Card className="text-center max-w-md">
        <CardBody className="py-12 space-y-4">
          <h1
            className="text-2xl font-bold"
            data-testid="route-error-boundary-heading"
          >
            Something went wrong
          </h1>
          <p
            className="text-default-500"
            data-testid="route-error-boundary-message"
          >
            We’ve been notified. You can try again or return home.
          </p>
        </CardBody>
        <Divider />
        <CardFooter className="justify-center gap-4">
          <Button
            color="primary"
            data-testid="route-error-boundary-try-again"
            onPress={() => {
              // A missing chunk from an old deploy can't be fixed by
              // re-rendering; only a full reload fetches the new build.
              if (isChunkLoadError(error)) window.location.reload();
              else reset();
            }}
          >
            Try again
          </Button>
          <Button
            as="a"
            data-testid="route-error-boundary-return-home"
            href={routes.public.home()}
            variant="bordered"
          >
            Return home
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
